import { useCallback, useEffect, useMemo, useState } from 'react';

import { PlayIcon, SaveIcon, WebhookIcon } from 'lucide-react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { IntegrationType } from '@/shared/types/integration';
import {
  WebhookType,
  type PreNbaWebhookTest,
  type WebhookPayload,
  type WebhookTest,
  type WebhookTestResultsResponse,
} from '@/shared/types/webhook';

import WebhookConfigTabs, { type WebhookTabKey } from './WebhookConfigTabs';
import { WebhookJsonTabContent } from './WebhookJsonTabContent';
import {
  formatTestPayload,
  parseJsonPayload,
  sanitizeHeaders,
  toTestParameters,
} from './webhookJsonUtils';
import { WebhookSetupTabContent } from './WebhookSetupTabContent';
import { WebhookTestResults as TestResults } from './WebhookTestResults';
import type { WebhookFormCommon } from './webhookTypes';

type ValidationMarker = { severity?: number };
type WebhookConfigFormValues = WebhookFormCommon & { testPayload?: string };

const DEFAULT_TEST_PAYLOAD = formatTestPayload();

const PRE_NBA_TEST_PAYLOAD = JSON.stringify(
  {
    subscriber_id: '00000000-0000-0000-0000-000000000000',
    campaign_id: '00000000-0000-0000-0000-000000000000',
    phone: '+1234567890',
    email: 'contact@example.com',
    context: { external_loan_id: 'LOAN-1234', segment: 'high_value', language: 'en' },
  },
  null,
  2
);

/** The envelope every outbound webhook body carries, whatever its type. */
const WEBHOOK_BODY_ENVELOPE = {
  channel: IntegrationType.WEB,
  channel_type: 'chat',
  session_id: '00000000-0000-0000-0000-000000000000',
  desk_id: '00000000-0000-0000-0000-000000000000',
  integration_id: '00000000-0000-0000-0000-000000000000',
  brain_id: '00000000-0000-0000-0000-000000000000',
  lang: 'en',
  context: { ssn: '123-45-6789' },
  timestamp: 1700000000000,
};

const AUTH_PRE_ENTER_TEST_PAYLOAD = JSON.stringify(WEBHOOK_BODY_ENVELOPE, null, 2);

const AUTH_VALIDATION_TEST_PAYLOAD = JSON.stringify(
  { ...WEBHOOK_BODY_ENVELOPE, value: '123-45-6789' },
  null,
  2
);

const getDefaultPayloadForType = (type: WebhookType): string => {
  if (type === WebhookType.PRE_NBA) {
    return PRE_NBA_TEST_PAYLOAD;
  }
  if (type === WebhookType.AUTH_PRE_ENTER) {
    return AUTH_PRE_ENTER_TEST_PAYLOAD;
  }
  if (type === WebhookType.AUTH_VALIDATION) {
    return AUTH_VALIDATION_TEST_PAYLOAD;
  }
  return DEFAULT_TEST_PAYLOAD;
};

const KNOWN_DEFAULT_PAYLOADS = new Set([
  DEFAULT_TEST_PAYLOAD,
  PRE_NBA_TEST_PAYLOAD,
  AUTH_PRE_ENTER_TEST_PAYLOAD,
  AUTH_VALIDATION_TEST_PAYLOAD,
]);

export interface WebhookConfigCardProps {
  defaultTab?: WebhookTabKey;
  className?: string;
  onTest?: (payload: WebhookTest | PreNbaWebhookTest) => void;
  onUpdateVariables?: () => void;
  onSavePayload?: (payload: WebhookPayload) => void;
  isTesting?: boolean;
  testResults?: WebhookTestResultsResponse | null;
  isCreateMode?: boolean;
  canWrite?: boolean;
  isSaving?: boolean;
  webhookType: WebhookType;
}

type AuthResponseHelp = { prefixKey: string; snippet: string; suffixKey: string };

const getAuthResponseHelp = (webhookType?: WebhookType): AuthResponseHelp | null => {
  if (webhookType === WebhookType.AUTH_VALIDATION) {
    return {
      prefixKey: 'webhook.auth_validation_response_help_prefix',
      snippet: '{ "valid": true | false }',
      suffixKey: 'webhook.auth_validation_response_help_suffix',
    };
  }
  if (webhookType === WebhookType.AUTH_PRE_ENTER) {
    return {
      prefixKey: 'webhook.auth_pre_enter_response_help_prefix',
      snippet: '{ "output": { "<context_var>": <value> } }',
      suffixKey: 'webhook.auth_pre_enter_response_help_suffix',
    };
  }
  return null;
};

export default function WebhookConfigCard({
  defaultTab = 'setup',
  className = '',
  onTest,
  onUpdateVariables,
  onSavePayload,
  isTesting = false,
  testResults = null,
  isCreateMode = false,
  canWrite = true,
  isSaving = false,
  webhookType,
}: WebhookConfigCardProps) {
  const [activeTab, setActiveTab] = useState<WebhookTabKey>(defaultTab);
  const [hasJsonValidationErrors, setHasJsonValidationErrors] = useState(false);
  const { t } = useTranslation();
  const { control, setValue, handleSubmit } = useFormContext<WebhookConfigFormValues>();
  const { isValid } = useFormState({ control });

  const watchedPayload = useWatch({ control, name: 'testPayload' });
  const expectedDefault = getDefaultPayloadForType(webhookType);

  // Initialize payload, or swap when type changes IF the user hasn't edited it.
  useEffect(() => {
    if (watchedPayload === undefined) {
      setValue('testPayload', expectedDefault, { shouldDirty: false, shouldTouch: false });
      return;
    }
    if (watchedPayload !== expectedDefault && KNOWN_DEFAULT_PAYLOADS.has(watchedPayload)) {
      setValue('testPayload', expectedDefault, { shouldDirty: false, shouldTouch: false });
    }
  }, [setValue, watchedPayload, expectedDefault]);

  const jsonValue = typeof watchedPayload === 'string' ? watchedPayload : expectedDefault;
  const parsedJson = useMemo(() => parseJsonPayload(jsonValue), [jsonValue]);

  const getJsonErrorMessage = () => {
    if (!parsedJson.isValid) {
      return 'Invalid JSON';
    }
    if (hasJsonValidationErrors) {
      return t('webhook.config.resolve_json_validation');
    }
    return null;
  };
  const jsonErrorMessage = getJsonErrorMessage();

  const handleJsonChange = useCallback(
    (nextValue?: string) => {
      setValue('testPayload', nextValue ?? '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    },
    [setValue]
  );

  const handleJsonValidate = useCallback((markers: ValidationMarker[]) => {
    const hasBlockingMarker = markers.some((marker) => (marker.severity ?? 0) >= 4);
    setHasJsonValidationErrors(hasBlockingMarker);
  }, []);

  const isPreNba = webhookType === WebhookType.PRE_NBA;

  const handleTest = useCallback(() => {
    if (!onTest || !parsedJson.isValid || !parsedJson.payload) {
      return;
    }

    void handleSubmit((values) => {
      const headers = values.includeHeaders ? sanitizeHeaders(values.headers) : [];
      const webhook = { url: values.url ?? '', token: values.token ?? '', headers };
      const params = parsedJson.payload;
      if (!params) {
        return;
      }

      if (isPreNba) {
        // The JSON-tab schema enforces these before the button enables; this guard
        // keeps a malformed payload from reaching the backend. Tag the webhook with
        // its type so orchestrator routes the test to NBA. PreNba payloads are
        // domain-specific (not the WebhookPayload body shape), so read them loosely.
        const nbaParams = params as Record<string, unknown>;
        if (
          typeof nbaParams.subscriber_id !== 'string' ||
          typeof nbaParams.campaign_id !== 'string'
        ) {
          return;
        }
        onTest({
          webhook: { ...webhook, type: WebhookType.PRE_NBA },
          test_parameters: {
            subscriber_id: nbaParams.subscriber_id,
            campaign_id: nbaParams.campaign_id,
            phone: typeof nbaParams.phone === 'string' ? nbaParams.phone : undefined,
            email: typeof nbaParams.email === 'string' ? nbaParams.email : undefined,
            context:
              typeof nbaParams.context === 'object' && nbaParams.context !== null
                ? (nbaParams.context as Record<string, unknown>)
                : {},
          },
        });
        return;
      }

      // For ACTION/MESSAGE types the editor holds the webhook body (WebhookPayload);
      // transform it to the orchestrator's test_parameters shape at test time.
      onTest({ webhook, test_parameters: toTestParameters(params) });
    })();
  }, [handleSubmit, isPreNba, onTest, parsedJson]);

  const isAuthType =
    webhookType === WebhookType.AUTH_VALIDATION || webhookType === WebhookType.AUTH_PRE_ENTER;
  const isTestUnsupported = isAuthType;

  const testUnsupportedTooltipKey = isAuthType ? 'webhook.test_webhook_auth_unsupported' : '';

  const handleSavePayload = useCallback(() => {
    if (!onSavePayload || !parsedJson.isValid || !parsedJson.payload) {
      return;
    }
    onSavePayload(parsedJson.payload);
  }, [onSavePayload, parsedJson]);

  const isTestDisabled =
    isTestUnsupported ||
    !onTest ||
    isTesting ||
    !isValid ||
    hasJsonValidationErrors ||
    !parsedJson.isValid;

  const authHelp = getAuthResponseHelp(webhookType);

  const isTestEmpty = !isTesting && !testResults;

  const testActions = (
    <InfoTooltip
      content={isTestUnsupported ? t(testUnsupportedTooltipKey) : ''}
      open={isTestUnsupported ? undefined : false}
      side="bottom"
      showArrow
    >
      {/* Span wrapper catches hover for disabled Button — the Button has
          disabled:pointer-events-none, so without this wrapper the tooltip
          never fires when isTestDisabled. */}
      <span className="inline-flex">
        <Button
          type="button"
          variant="outline-neutral"
          size="sm"
          disabled={isTestDisabled}
          isLoading={isTesting}
          onClick={handleTest}
          data-testid="send_test_button"
        >
          <PlayIcon className="h-4 w-4" />
          {t('common.test')}
        </Button>
      </span>
    </InfoTooltip>
  );

  const testMessages = (
    <>
      {jsonErrorMessage && <p className="text-sm text-destructive">{jsonErrorMessage}</p>}
      {authHelp && (
        <div className="rounded-md border border-informational/30 bg-informational/10 p-3 text-sm text-foreground">
          {t(authHelp.prefixKey)}{' '}
          <code className="rounded bg-bg-muted px-1.5 py-0.5 font-mono text-xs">
            {authHelp.snippet}
          </code>{' '}
          {t(authHelp.suffixKey)}
        </div>
      )}
    </>
  );

  return (
    <Card
      className={cn(
        'grid h-full grid-cols-2 gap-6 rounded-lg border-none bg-bg-muted p-1 shadow-none',
        className
      )}
    >
      {/* Config tabs section */}
      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-stroke bg-background p-6 pt-7">
        <WebhookConfigTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          setupContent={
            <WebhookSetupTabContent
              autoFocusUrl={isCreateMode}
              showToolSettings={!isCreateMode && webhookType === WebhookType.ACTION}
              canWrite={canWrite}
            />
          }
          jsonContent={
            <div className="flex h-full flex-col gap-3">
              <WebhookJsonTabContent
                value={jsonValue}
                onChange={handleJsonChange}
                onValidate={handleJsonValidate}
                webhookType={webhookType}
              />
              {!isCreateMode && onSavePayload && (
                <Button
                  type="button"
                  variant="outline-neutral"
                  size="sm"
                  className="self-end"
                  disabled={!parsedJson.isValid || hasJsonValidationErrors || !canWrite}
                  isLoading={isSaving}
                  onClick={handleSavePayload}
                >
                  <SaveIcon className="h-4 w-4" />
                  {t('webhook.test.save_payload')}
                </Button>
              )}
            </div>
          }
        />
      </div>

      {/* Test section */}
      <div className="flex min-w-0 flex-col gap-6 overflow-auto p-6 pt-7">
        {isTestEmpty ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <WebhookIcon aria-hidden />
              </EmptyMedia>
              <EmptyTitle>{t('webhook.test_title')}</EmptyTitle>
              <EmptyDescription>{t('webhook.test_empty_description')}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <div className="flex items-center gap-3">{testActions}</div>
              {testMessages}
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold tracking-tight">{t('webhook.test_title')}</h3>
                <div className="flex items-center gap-2">{testActions}</div>
              </div>

              {testMessages}
            </div>

            {isTesting ? (
              <div className="flex flex-1 flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <div className="h-4 w-96 rounded bg-muted" />
                  <div className="h-[45px] w-full rounded-md bg-muted" />
                </div>
                <div className="flex-1 rounded-xl bg-muted" />
              </div>
            ) : (
              <TestResults
                testResults={testResults}
                onRefresh={onUpdateVariables}
                isCreateMode={isCreateMode}
                webhookType={webhookType}
              />
            )}
          </>
        )}
      </div>
    </Card>
  );
}
